/**
 * AIFieldConfidenceBadge
 *
 * Tiny inline badge shown next to form field labels when AI has auto-filled
 * the field. Disappears when the user edits the field (via MARK_FIELD_REVIEWED).
 *
 * Blue  (≥60% confidence) → green  "90% ✓"
 * Yellow (30–59%)         → amber  "72% ?"
 * Red   (<30%)            → red    "45% !"
 */
import React from 'react';
import { Tag } from 'antd';
import { motion } from 'framer-motion';
import type { AISuggestion } from '../contexts/EstimateFormContext';

export interface AIFieldConfidenceBadgeProps {
  confidence: number;
  band: AISuggestion['band'];
}

const BAND_CONFIG: Record<
  Exclude<AISuggestion['band'], 'required_missing'>,
  { color: string; icon: string; label: string }
> = {
  blue: { color: 'success', icon: '✓', label: 'Auto-filled' },
  yellow: { color: 'warning', icon: '?', label: 'Review' },
  red: { color: 'error', icon: '!', label: 'Low' },
};

export const AIFieldConfidenceBadge: React.FC<AIFieldConfidenceBadgeProps> = ({
  confidence,
  band,
}) => {
  if (band === 'required_missing') return null;

  const cfg = BAND_CONFIG[band];
  const pct = Math.round(confidence * 100);

  return (
    <motion.span
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0, opacity: 0 }}
      transition={{ type: 'spring', stiffness: 500, damping: 20 }}
      style={{ display: 'inline-flex', marginLeft: '6px', verticalAlign: 'middle' }}
    >
      <Tag
        color={cfg.color}
        style={{
          fontSize: '10px',
          lineHeight: '16px',
          padding: '0 5px',
          margin: 0,
          borderRadius: '8px',
          cursor: 'default',
          userSelect: 'none',
        }}
      >
        {pct}% {cfg.icon}
      </Tag>
    </motion.span>
  );
};
